// React and related imports
import React from 'react';

// Flowbite components
import { Modal, Button } from 'flowbite-react';

// Interfaces and Enums
import { IMedia } from '../../common/interfaces/Media.interface';
import { MediaTypes } from '../../common/enums/MediaTypes';

// Utils
import { localizeMediaType } from '../../utils/localizeMediaType';

// i18 hooks
import { useTranslation } from 'react-i18next';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

interface MediaPreviewProps {
  item: IMedia | null;
  onClose: () => void;
  onSelect: () => void;
}

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  item,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  if (!item) return null;

  const renderPreview = () => {
    {
      switch (item.type) {
        case MediaTypes.IMAGE:
          return (
            <img
              src={item.url}
              alt={item.name}
              className="max-h-[50vh] rounded-lg object-contain shadow-2xl"
            />
          );
        case MediaTypes.VIDEO:
          return (
            <video
              src={item.url}
              controls
              className="max-h-[50vh] rounded-lg shadow-2xl"
            />
          );
        case MediaTypes.SOUND:
          return (
            <audio controls src={item.url} className="mx-auto w-full"></audio>
          );
        case MediaTypes.DOCUMENT:
          return (
            <iframe
              src={item.url}
              title={item.name}
              className="h-[500px] w-full"
            ></iframe>
          );
        default:
          return null;
      }
    }
  };
  return (
    <Modal show={!!item} onClose={onClose}>
      <Modal.Header className="modal-header !p-3">
        <span className="font-semibold">{t('content.preview')}</span>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-grow items-center justify-center px-2 py-0">
          {renderPreview()}
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between">
        <h3 className="w-1/2 truncate font-medium dark:text-white">
          {item.name}
        </h3>
        <Button onClick={onSelect} type="button" className={buttonClass}>
          {t('content.select') +
            ' ' +
            localizeMediaType(item.type as MediaTypes)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
